import env from 'helpers/env';
import history from 'helpers/history';
import { markAnalyticsReady } from 'helpers/pageEntry';

import * as Sentry from '@sentry/react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Identify, createInstance } from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';

const instance = createInstance();

// Track event with Amplitude
export const amplitudeTrackEvent = (
  eventName: string,
  eventProperties: any
) => {
  instance.logEvent(eventName, eventProperties);
};

// Guides and Surveys links set to "Use router" navigate in-app instead of
// reloading the page.
const navigateFromGuide = (url: string) => {
  let target: URL;
  try {
    target = new URL(url, window.location.href);
  } catch {
    return;
  }

  if (target.protocol !== 'https:' && target.protocol !== 'http:') {
    return;
  }

  if (target.origin !== window.location.origin) {
    window.location.assign(target.href);
    return;
  }

  history.push(`${target.pathname}${target.search}${target.hash}`);
};

// The SDK's init starts a new session and re-adds its page-view and
// autocapture plugins each time it runs, and useAmplitude calls this on every
// render, so init only once per page load. That also keeps the Guides and
// Surveys plugin from being dropped or booted twice.
let isInitialized = false;
let currentUserId: string | null = null;

// Initialize Amplitude instance
export const initAmplitude = (userEmail: string) => {
  if (!isInitialized) {
    isInitialized = true;
    // @ts-ignore
    const apiKey: string = env.amplitudeWriteKey;

    instance
      .init(apiKey, {
        autocapture: {
          attribution: false,
          pageViews: true,
          sessions: true,
          elementInteractions: true
        }
      })
      .promise.then(() => {
        // Added after init on purpose: init waits for the setup of every
        // plugin added before it, and this plugin's setup waits for the Guides
        // and Surveys script, so adding it first would hold all analytics until
        // that script loads. The trade-off is that events tracked before this
        // point, such as the first page view, don't reach Guides and Surveys.
        const added = instance.add(engagementPlugin());
        window.engagement?.setRouter(navigateFromGuide);
        return added.promise;
      })
      .catch(error => {
        // Analytics must not break the app, but the failure must be reported.
        Sentry.captureException(error);
      });
  }

  if (userEmail && userEmail !== currentUserId) {
    currentUserId = userEmail;
    instance.setUserId(userEmail);
  }

  // Only the first call counts; the current page is the landing page.
  markAnalyticsReady(window.location.pathname, amplitudeTrackEvent);
};

// Clear the identity when the session ends so analytics and Guides and
// Surveys targeting stop using the previous user. logout() and the 401
// handlers in helpers/http.js and helpers/http2.ts call this, sometimes twice
// for one 401, so it does nothing once the identity is already cleared.
export const resetAmplitude = () => {
  if (!isInitialized || currentUserId === null) {
    return;
  }
  currentUserId = null;
  instance.reset();
};

export interface IAmplitudeUserProperties {
  organization_uuid?: string;
  plan_id?: string | number;
}

// Ids only: never names, emails or the org domain.
export const setAmplitudeUserProperties = (
  properties: IAmplitudeUserProperties
) => {
  const identify = new Identify();
  let hasProperty = false;

  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    identify.set(key, value);
    hasProperty = true;
  });

  if (hasProperty) {
    instance.identify(identify);
  }
};
